import { storableError } from '../../util/errors';
import { 
  fetchArtArtists,
  getArtArtists,
  isArtArtistsFetchInProgress,
  getArtArtistsError,
} from '../../ducks/artistUsernames.duck';

// ================ Action types ================ //

export const FETCH_ARTISTS_REQUEST = 'app/ArtistDirectoryPage/FETCH_ARTISTS_REQUEST';
export const FETCH_ARTISTS_SUCCESS = 'app/ArtistDirectoryPage/FETCH_ARTISTS_SUCCESS';
export const FETCH_ARTISTS_ERROR = 'app/ArtistDirectoryPage/FETCH_ARTISTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  artists: [],
  fetchArtistsError: null,
  fetchArtistsInProgress: false,
};

export default function artistDirectoryPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ARTISTS_REQUEST:
      return {
        ...state,
        fetchArtistsInProgress: true,
        fetchArtistsError: null,
      };
    case FETCH_ARTISTS_SUCCESS:
      return {
        ...state,
        artists: payload,
        fetchArtistsInProgress: false,
      };
    case FETCH_ARTISTS_ERROR:
      return {
        ...state,
        fetchArtistsError: payload,
        fetchArtistsInProgress: false,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchArtistsRequest = () => ({ type: FETCH_ARTISTS_REQUEST });
export const fetchArtistsSuccess = artists => ({
  type: FETCH_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchArtistsError = e => ({
  type: FETCH_ARTISTS_ERROR,
  payload: e,
});

// ================ Thunks ================ //

export const fetchArtists = () => (dispatch, getState, sdk) => {
  dispatch(fetchArtistsRequest());

  // Get art artists from the global state
  const state = getState();
  const artArtists = getArtArtists(state);
  const isGlobalFetchInProgress = isArtArtistsFetchInProgress(state);
  const globalError = getArtArtistsError(state);

  // If art artists are already available, use them immediately
  if (artArtists && artArtists.length > 0) {
    dispatch(fetchArtistsSuccess(artArtists));
    return Promise.resolve(artArtists);
  }

  // If there's an error from the global fetch, propagate it
  if (globalError && !isGlobalFetchInProgress) {
    dispatch(fetchArtistsError(globalError));
    return Promise.reject(globalError);
  }

  // If global fetch is in progress, wait for it
  if (isGlobalFetchInProgress) {
    // Return a promise that resolves when the global fetch completes
    return new Promise((resolve, reject) => {
      const checkForData = () => {
        const currentState = getState();
        const currentArtists = getArtArtists(currentState);
        const currentError = getArtArtistsError(currentState);
        const currentInProgress = isArtArtistsFetchInProgress(currentState);

        if (currentArtists && currentArtists.length > 0) {
          dispatch(fetchArtistsSuccess(currentArtists));
          resolve(currentArtists);
        } else if (currentError && !currentInProgress) {
          dispatch(fetchArtistsError(currentError));
          reject(currentError);
        } else if (currentInProgress) {
          // Still in progress, check again in a bit
          setTimeout(checkForData, 100);
        } else {
          // No data, no error, not in progress - something went wrong
          const error = storableError(new Error('Failed to fetch art artists'));
          dispatch(fetchArtistsError(error));
          reject(error);
        }
      };
      checkForData();
    });
  }

  // If no data and not in progress, trigger art-specific fetch
  return dispatch(fetchArtArtists())
    .then(() => {
      const updatedState = getState();
      const fetchedArtists = getArtArtists(updatedState);
      dispatch(fetchArtistsSuccess(fetchedArtists));
      return fetchedArtists;
    })
    .catch(e => {
      dispatch(fetchArtistsError(storableError(e)));
      throw e;
    });
};

// ================ Page load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Fetch art category artists specifically for this page
  return dispatch(fetchArtArtists());
}; 